import React, { ForwardedRef } from 'react';
import Icon, { localIcon } from "components/elements/Icon";
import cn from "classnames";
import Text from "components/typography/Text";

export type buttonStyles =
    "default"
    | "warning"
    | "success"
    | "danger"
    | "ghost"
    | "primary";

export type buttonSizes = "small" | "normal" | "large" | "wide";

export type ButtonProps = {
    style?: buttonStyles;
    colors?: {
        background: string;
        text: string;
    }
    backgroundColor?: string;
    size?: buttonSizes;
    className?: string;
    onClick?: any;
    label: string;
    disabled: boolean;
    type?: "submit" | "button";
    pending?: boolean;
    iconOnly?: boolean;
    hidden?: boolean;
};

const PlanButton = React.forwardRef((props: ButtonProps, ref: ForwardedRef<any>) => {
    const { style, label, onClick, disabled, type, pending, iconOnly, colors } = props;
    const size = props.size === "wide" ? "large" : props.size;

    const buttonCn = cn(
        "active:shadow-primary-active",
        "transition-all duration-150 cursor-pointer",
    );

    const sizeCn = cn(
        { "py-1.5 px-3 rounded-lg": size == "small" },
        { "py-2 px-4 rounded-lg": size == "normal" },
        { "py-3 px-6 rounded-lg": size == "large" }
    );

    const styleCn = cn(
        { "border border-primary-50 bg-white hover:bg-primary-25 active:bg-primary-50": style == "default" },
        { "hover:bg-primary-50/50": style == "ghost" },
        { "border border-error-50 hover:bg-error-100": style == "warning" },
        { "border border-[#00211117] hover:bg-success-500 bg-success-400": style == "success" },
        { "border border-[#00211117] hover:bg-red bg-error-500": style == "danger" },
        { "border border-[#00211117] hover:bg-primary-700 bg-primary-600": style == "primary" },
    );

    const textColorCn = cn(
        { 'text-primary-600': style == "default" },
        { "text-error-500": style == "warning" },
        { "text-white": style == "success" },
        { "text-white": style == "danger" },
        { "text-white": style == "primary" }
    );

    const iconCn = cn(
        { "bg-gray-900": style == "default" },
        { "bg-gray-900": style == "ghost" },
        { "bg-error-500": style == "warning" },
        { "bg-white": style == "success" },
        { "bg-white": style == "danger" },
        { "bg-white": style == "primary" }
    );

    const classes = cn(
        "button-tailwind",
        buttonCn,
        sizeCn,
        styleCn,
        props.className,

    );

    const Label = () => {
        if (size == "small") {
            return (
                <Text
                    size={"sm"}
                    weight={"normal"}
                    color={textColorCn}
                    className={'mx-auto'}

                >
                    {label}
                </Text>
            );
        }

        if (size == "normal") {
            return (
                <Text
                    size={"md"}
                    weight={"semibold"}
                    color={textColorCn}
                    className={'mx-auto'}

                >
                    {label}
                </Text>
            );
        }

        if (size == "large") {
            return (
                <Text
                    size={"lg"}
                    weight={"normal"}
                    color={textColorCn}
                    className={'mx-auto'}
                >
                    {label}
                </Text>
            );
        }

        return null;
    };

    return (
        <button
            ref={ref}
            hidden={props.hidden}
            className={classes}
            disabled={disabled || pending}
            type={type ? type : "button"}
            style={colors !== undefined ? {
                backgroundColor: colors.background,
                color: colors.text
            } : null}
            onClick={onClick}
        >
            {!iconOnly && (
                <Label/>
            )}
        </button>
    );
});

PlanButton.displayName = "Button";
PlanButton.defaultProps = {
    style: "default",
    size: "normal"
};

export default PlanButton;